import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CheckCircle, DollarSign, Users, TrendingUp, Search, Download, Plus, Eye, MessageSquare, Phone, Mail, Calculator, FileText, X } from 'lucide-react';

const DisputesRefunds = () => {
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  // Statistiques des litiges
  const stats = {
    activeDisputes: 23,
    pendingRefunds: 8,
    avgResolutionTime: "2.3 jours",
    satisfactionRate: 94,
    totalRefunds: "€45,670"
  };

  // Litiges critiques
  const criticalDisputes = [
    {
      id: 'DIS-2024-001',
      client: 'Sophie Martin',
      property: 'Villa Azure',
      amount: '€24,500',
      reason: 'Problème équipement villa',
      sla: '2h restantes',
      priority: 'Critique'
    },
    {
      id: 'DIS-2024-007',
      client: 'Jean Dubois',
      property: 'Yacht Serenity',
      amount: '€18,900',
      reason: 'Annulation météo',
      sla: '4h restantes',
      priority: 'Haute'
    }
  ];

  // Liste des litiges
  const disputes = [
    {
      id: 'DIS-2024-001',
      client: 'Sophie Martin',
      property: 'Villa Azure',
      amount: 24500,
      type: 'Équipement',
      reason: 'Problème avec la piscine chauffée',
      priority: 'Critique',
      status: 'En cours',
      sla: '2h',
      slaStatus: 'danger',
      dateCreated: '2024-09-12',
      clientProfile: {
        name: 'Sophie Martin',
        tier: 'Platinum',
        totalSpent: '€156,780',
        reservations: 12,
        satisfaction: 4.8
      },
      timeline: [
        { date: '2024-09-12 10:30', event: 'Litige créé', author: 'Client' },
        { date: '2024-09-12 11:15', event: 'Première réponse envoyée', author: 'Marie L.' },
        { date: '2024-09-12 14:20', event: 'Inspection villa programmée', author: 'Tech Team' },
        { date: '2024-09-12 16:45', event: 'En attente de rapport technique', author: 'System' }
      ],
      communications: [
        {
          timestamp: '10:30',
          sender: 'Sophie Martin',
          message: 'Bonjour, la piscine n\'est pas chauffée comme promis dans le contrat. Nous sommes très déçus.',
          type: 'client'
        },
        {
          timestamp: '11:15',
          sender: 'Marie Laurent',
          message: 'Bonjour Sophie, nous sommes désolés de ce désagrément. Un technicien va intervenir dans l\'heure qui suit.',
          type: 'agent'
        },
        {
          timestamp: '14:20',
          sender: 'Sophie Martin',
          message: 'Le technicien est passé mais dit que la réparation prendra 24h. Nous voulons une compensation.',
          type: 'client'
        }
      ]
    },
    {
      id: 'DIS-2024-002',
      client: 'Jean-Marc Dubois',
      property: 'Château Provence',
      amount: 18900,
      type: 'Service',
      reason: 'Chef privé non disponible',
      priority: 'Moyenne',
      status: 'En révision',
      sla: '1j 4h',
      slaStatus: 'warning',
      dateCreated: '2024-09-11'
    },
    {
      id: 'DIS-2024-003',
      client: 'Isabella Romano',
      property: 'Villa Toscane',
      amount: 32100,
      type: 'Annulation',
      reason: 'Force majeure - restrictions COVID',
      priority: 'Haute',
      status: 'Résolu',
      sla: 'Respecté',
      slaStatus: 'success',
      dateCreated: '2024-09-09'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Critique': return 'text-red-600 bg-red-100';
      case 'En cours': return 'text-blue-600 bg-blue-100';
      case 'En révision': return 'text-yellow-600 bg-yellow-100';
      case 'Résolu': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critique': return 'text-red-600 bg-red-100';
      case 'Haute': return 'text-orange-600 bg-orange-100';
      case 'Moyenne': return 'text-yellow-600 bg-yellow-100';
      case 'Basse': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSlaColor = (slaStatus) => {
    switch (slaStatus) {
      case 'danger': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'success': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const calculateRefund = (dispute) => {
    // Logique de calcul du remboursement basée sur le type et la situation
    const baseAmount = dispute.amount;
    let refundPercentage = 0;

    switch (dispute.type) {
      case 'Équipement':
        refundPercentage = 25; // 25% du montant
        break;
      case 'Service':
        refundPercentage = 15; // 15% du montant
        break;
      case 'Annulation':
        refundPercentage = 100; // Remboursement complet pour force majeure
        break;
      default:
        refundPercentage = 10;
    }

    return Math.round(baseAmount * (refundPercentage / 100));
  };

  const DisputeDetailsPanel = () => {
    if (!showDetailsPanel || !selectedDispute) return null;

    const dispute = disputes.find(d => d.id === selectedDispute);
    if (!dispute) return null;

    const suggestedRefund = calculateRefund(dispute);

    return (
      <div className="fixed inset-y-0 right-0 w-1/2 bg-white border-l border-gray-200 shadow-xl z-40 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Détails du Litige</h3>
            <button
              onClick={() => setShowDetailsPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Alerte SLA */}
          {dispute.slaStatus === 'danger' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="text-red-600" size={20} />
                <span className="font-medium text-red-800">SLA Critique - {dispute.sla} restantes</span>
              </div>
              <p className="text-sm text-red-700 mt-1">
                Ce litige nécessite une résolution immédiate pour respecter nos engagements.
              </p>
            </div>
          )}

          {/* Informations du Cas */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Informations du Cas</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">ID Litige:</span>
                <span className="font-medium">{dispute.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Client:</span>
                <span className="font-medium">{dispute.client}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Propriété:</span>
                <span className="font-medium">{dispute.property}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Montant:</span>
                <span className="font-medium">€{dispute.amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium">{dispute.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Priorité:</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(dispute.priority)}`}>
                  {dispute.priority}
                </span>
              </div>
            </div>
          </div>

          {/* Profil Client */}
          {dispute.clientProfile && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Profil Client</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut:</span>
                  <span className="text-yellow-600 font-medium">{dispute.clientProfile.tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total dépensé:</span>
                  <span className="font-medium">{dispute.clientProfile.totalSpent}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Réservations:</span>
                  <span className="font-medium">{dispute.clientProfile.reservations}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Satisfaction:</span>
                  <span className="font-medium">{dispute.clientProfile.satisfaction}/5</span>
                </div>
              </div>
            </div>
          )}

          {/* Calcul de Résolution */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-green-800 mb-3">Calcul de Résolution</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-green-700">Montant du séjour:</span>
                <span className="font-medium">€{dispute.amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Remboursement suggéré:</span>
                <span className="font-medium">€{suggestedRefund.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">Pourcentage:</span>
                <span className="font-medium">{Math.round((suggestedRefund / dispute.amount) * 100)}%</span>
              </div>
            </div>
          </div>

          {/* Communications */}
          {dispute.communications && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Communications</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {dispute.communications.map((comm, index) => (
                  <div key={index} className={`p-3 rounded-lg ${
                    comm.type === 'client' ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50 border-l-4 border-gray-500'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-sm">{comm.sender}</span>
                      <span className="text-xs text-gray-500">{comm.timestamp}</span>
                    </div>
                    <p className="text-sm">{comm.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          {dispute.timeline && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Chronologie</h4>
              <div className="space-y-3">
                {dispute.timeline.map((event, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{event.event}</p>
                      <p className="text-xs text-gray-600">{event.date} • {event.author}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Actions</h4>
            <div className="space-y-2">
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                <Phone size={16} />
                <span>Appeler le client</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-green-600 border border-green-300 rounded-lg hover:bg-green-50">
                <Mail size={16} />
                <span>Envoyer email</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                <MessageSquare size={16} />
                <span>Envoyer message</span>
              </button>
              <button className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50">
                <AlertTriangle size={16} />
                <span>Escalader le cas</span>
              </button>
            </div>
          </div>

          {/* Formulaire de Résolution */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Résolution</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de résolution</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500">
                  <option>Remboursement partiel</option>
                  <option>Remboursement complet</option>
                  <option>Avoir/Crédit</option>
                  <option>Compensation service</option>
                  <option>Aucune compensation</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€)</label>
                  <input
                    type="number"
                    defaultValue={suggestedRefund}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pourcentage</label>
                  <input
                    type="number"
                    defaultValue={Math.round((suggestedRefund / dispute.amount) * 100)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes internes</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                  placeholder="Détails de la résolution..."
                ></textarea>
              </div>

              <div className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500" />
                <label className="text-sm text-gray-700">Notifier automatiquement le client</label>
              </div>

              <button className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                Confirmer la Résolution
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="disputes-refunds">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Litiges & Remboursements</h1>
            <p className="text-gray-600 mt-1">Gestion centralisée des litiges clients et processus de remboursement</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download size={18} />
              <span>Exporter Rapport</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Litige</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Alerte Priorité */}
        {criticalDisputes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-lg font-semibold text-red-800">Litiges Critiques</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {criticalDisputes.map((dispute) => (
                <div key={dispute.id} className="bg-white border border-red-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-800">{dispute.id}</h4>
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      {dispute.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{dispute.client} • {dispute.property}</p>
                  <p className="text-sm text-gray-700 mb-2">{dispute.reason}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-800">{dispute.amount}</span>
                    <span className="text-sm text-red-600 font-medium">SLA: {dispute.sla}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Aperçu Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-red-600" size={24} />
              <span className="text-sm font-medium text-red-600">+15.3%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.activeDisputes}</div>
            <div className="text-sm text-gray-600">Litiges Actifs</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-orange-600" size={24} />
              <span className="text-sm font-medium text-orange-600">En attente</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.pendingRefunds}</div>
            <div className="text-sm text-gray-600">Remb. en Attente</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-blue-600" size={24} />
              <span className="text-sm font-medium text-green-600">-8.2%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.avgResolutionTime}</div>
            <div className="text-sm text-gray-600">Résolution Moyenne</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-green-600" size={24} />
              <span className="text-sm font-medium text-green-600">+2.1%</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.satisfactionRate}%</div>
            <div className="text-sm text-gray-600">Taux de Satisfaction</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-600">Ce mois</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.totalRefunds}</div>
            <div className="text-sm text-gray-600">Remboursements</div>
          </div>
        </div>

        {/* Actions Rapides */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <DollarSign size={18} />
              <span>Traiter Remboursements</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <FileText size={18} />
              <span>Modèles de Réponse</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
              <AlertTriangle size={18} />
              <span>Escalade Auto</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Calculator size={18} />
              <span>Calculateur Remboursement</span>
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un litige..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous statuts</option>
              <option value="critique">Critique</option>
              <option value="en-cours">En cours</option>
              <option value="en-revision">En révision</option>
              <option value="resolu">Résolu</option>
            </select>

            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous types</option>
              <option value="equipement">Équipement</option>
              <option value="service">Service</option>
              <option value="annulation">Annulation</option>
              <option value="facturation">Facturation</option>
            </select>

            <select 
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes priorités</option>
              <option value="critique">Critique</option>
              <option value="haute">Haute</option>
              <option value="moyenne">Moyenne</option>
              <option value="basse">Basse</option>
            </select>

            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>
        </div>

        {/* Tableau des Litiges */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Litige
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriété
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type/Raison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priorité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SLA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {disputes.map((dispute) => (
                  <tr key={dispute.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-700 cursor-pointer">
                      {dispute.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dispute.client}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dispute.property}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      €{dispute.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{dispute.type}</div>
                        <div className="text-sm text-gray-500">{dispute.reason}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(dispute.priority)}`}>
                        {dispute.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(dispute.status)}`}>
                        {dispute.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getSlaColor(dispute.slaStatus)}`}>
                        {dispute.sla}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedDispute(dispute.id);
                          setShowDetailsPanel(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Eye size={16} />
                      </button>
                      <button className="text-green-600 hover:text-green-900 mr-3">
                        <MessageSquare size={16} />
                      </button>
                      <button className="text-purple-600 hover:text-purple-900">
                        <Phone size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DisputeDetailsPanel />
    </div>
  );
};

export default DisputesRefunds;